<template>
  <div v-if="itemsToRender.length" class="payments-live-stack">
    <div class="payments-live-stack-header">
      <MTypography type="body" class="payments-live-stack-title">
        Payment Updates
      </MTypography>
      <MButton
        type="text"
        class="payments-live-stack-clear"
        @click="onClearHandler"
      >
        Clear
      </MButton>
    </div>

    <div class="payments-live-stack-list">
      <div
        v-for="item in itemsToRender"
        :key="item.id"
        class="payments-live-stack-item"
      >
        <div class="payments-live-stack-badge" />

        <div class="payments-live-stack-content">
          <MTypography type="label" class="payments-live-stack-item-title">
            New payment received
          </MTypography>
          <MTypography
            type="body"
            class="payments-live-stack-item-id"
            role="button"
            tabindex="0"
            @click="onOpenHandler(item.id)"
            @keydown.enter.prevent="onOpenHandler(item.id)"
          >
            {{ item.id }}
          </MTypography>
        </div>

        <div class="payments-live-stack-actions">
          <button
            type="button"
            class="copy-btn"
            :class="{ copied: copiedPaymentId === item.id }"
            @click.stop="onCopyHandler(item.id)"
            :aria-label="`Copy payment id ${item.id}`"
            title="Copy payment id"
          >
            <svg
              v-if="copiedPaymentId === item.id"
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M6.66667 11.3333L2.99999 7.66666L4.05555 6.6111L6.66667 9.22222L11.9444 3.94444L13 4.99999L6.66667 11.3333Z"
                fill="currentColor"
              />
            </svg>
            <svg
              v-else
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M6.33333 1.99999H12.6667C13.219 1.99999 13.6667 2.44771 13.6667 2.99999V9.33332C13.6667 9.8856 13.219 10.3333 12.6667 10.3333H6.33333C5.78105 10.3333 5.33333 9.8856 5.33333 9.33332V2.99999C5.33333 2.44771 5.78105 1.99999 6.33333 1.99999Z"
                stroke="currentColor"
                stroke-width="1.2"
              />
              <path
                d="M3.33333 5H2.99999C2.44771 5 1.99999 5.44772 1.99999 6V12.3333C1.99999 12.8856 2.44771 13.3333 2.99999 13.3333H9.33332C9.8856 13.3333 10.3333 12.8856 10.3333 12.3333V11.9999"
                stroke="currentColor"
                stroke-width="1.2"
                stroke-linecap="round"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { MButton, MTypography } from "@aeroglobe/proxima-design-system";

interface PaymentRealtimeItem {
  id: string;
}

defineOptions({
  name: "PaymentRealtimeNotificationStack",
});

const props = defineProps<{
  items: PaymentRealtimeItem[];
}>();

const emit = defineEmits<{
  (e: "open", paymentId: string): void;
  (e: "clear"): void;
}>();

const itemsToRender = computed<PaymentRealtimeItem[]>(() => props.items);

const onClearHandler = () => {
  emit("clear");
};

const copiedPaymentId = ref<string | null>(null);
const copiedTimeoutId = ref<ReturnType<typeof setTimeout> | null>(null);

const onOpenHandler = (paymentId: string) => {
  emit("open", paymentId);
};

const onCopyHandler = async (paymentId: string) => {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(paymentId);
    } else {
      // Fallback for older browsers / restricted contexts
      const textarea = document.createElement("textarea");
      textarea.value = paymentId;
      textarea.style.position = "fixed";
      textarea.style.left = "-9999px";
      textarea.style.top = "-9999px";
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
    }

    copiedPaymentId.value = paymentId;
    if (copiedTimeoutId.value) clearTimeout(copiedTimeoutId.value);
    copiedTimeoutId.value = setTimeout(() => {
      copiedPaymentId.value = null;
      copiedTimeoutId.value = null;
    }, 1500);
  } catch {
    // Silent fail: user can still click ID to open.
  }
};
</script>

<style scoped lang="css">
.payments-live-stack {
  position: fixed;
  top: 80px;
  right: 18px;
  width: min(340px, calc(100vw - 28px));
  z-index: 900;
  border-radius: 14px;
  background: color-mix(in srgb, #ffffff, transparent 0%);
  border: 1px solid color-mix(in srgb, var(--m-divider-color), transparent 0%);
  box-shadow:
    0 20px 42px color-mix(in srgb, #0f172a, transparent 82%),
    0 2px 8px color-mix(in srgb, #0f172a, transparent 88%);
  overflow: hidden;
}

.payments-live-stack::before {
  content: none;
}

.payments-live-stack-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 10px;
  border-bottom: 1px solid
    color-mix(in srgb, var(--m-divider-color), transparent 0%);
  background: transparent;
}

.payments-live-stack-title {
  font-weight: 600;
  letter-spacing: 0.1px;
  color: var(--m-dark-color);
}

.payments-live-stack-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-height: 320px;
  overflow-y: auto;
  padding: 8px;
}

.payments-live-stack-item {
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 7px;
  padding: 9px;
  border-radius: 10px;
  border: 1px solid color-mix(in srgb, var(--m-divider-color), transparent 0%);
  background: color-mix(in srgb, #ffffff, transparent 0%);
  transition: border-color 120ms ease;
}

.payments-live-stack-item:hover {
  border-color: color-mix(in srgb, var(--m-primary-color), transparent 0%);
}

.payments-live-stack-badge {
  width: 8px;
  height: 8px;
  margin-top: 6px;
  border-radius: 50%;
  background: var(--m-primary-color);
  box-shadow: 0 0 0 4px
    color-mix(in srgb, var(--m-primary-color), transparent 80%);
}

.payments-live-stack-content {
  min-width: 0;
}

.payments-live-stack-item-id {
  margin-top: 2px;
  font-weight: 600;
  color: var(--m-dark-color);
  letter-spacing: 0.2px;
  cursor: pointer;
  user-select: none;
}

.payments-live-stack-actions {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
}

.copy-btn {
  width: 30px;
  height: 30px;
  border-radius: 10px;
  border: 1px solid color-mix(in srgb, var(--m-divider-color), transparent 0%);
  background: color-mix(in srgb, #ffffff, transparent 0%);
  color: var(--m-dark-color);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
}

.copy-btn:hover {
  filter: brightness(0.98);
}

.copy-btn.copied {
  color: var(--m-success-color);
  border-color: color-mix(in srgb, var(--m-success-color), transparent 60%);
  background: color-mix(in srgb, var(--m-success-color), transparent 92%);
}
.payments-live-stack-item-title {
  font-weight: 600;
  color: var(--m-dark-color);
}
</style>
